import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { adminService } from '../services/adminService';

function fmtSeconds(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}m ${sec}s`;
}

function fmtMinutes(m) {
  return `${m} min`;
}

function SyncBadge({ inSync }) {
  if (inSync === null || inSync === undefined) return null;
  return inSync ? (
    <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-inter font-medium"
      style={{ backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: '12px' }}>
      In sync
    </span>
  ) : (
    <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-inter font-medium"
      style={{ backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '12px' }}>
      Out of sync
    </span>
  );
}

function EntryTypeBadge({ type }) {
  const isTopup = type === 'TOPUP';
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-inter font-medium"
      style={{
        backgroundColor: isTopup ? '#eff6ff' : '#fefce8',
        color: isTopup ? '#1d4ed8' : '#854d0e',
        border: `1px solid ${isTopup ? '#bfdbfe' : '#fef08a'}`,
        borderRadius: '12px',
      }}>
      {type}
    </span>
  );
}

function ConfirmModal({ title, message, onConfirm, onCancel, loading, confirmLabel = 'Confirm', confirmStyle }) {
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50"
      style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
      <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full mx-4"
        style={{ border: '1px solid #e5e7eb' }}>
        <h3 className="text-lg font-montserrat font-semibold mb-2" style={{ color: '#222324' }}>{title}</h3>
        <p className="text-sm font-inter mb-6" style={{ color: '#555' }}>{message}</p>
        <div className="flex justify-end space-x-3">
          <button onClick={onCancel} disabled={loading}
            className="px-4 py-2 text-sm font-inter rounded-lg"
            style={{ border: '1px solid #e5e7eb', color: '#555' }}>
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading}
            className="px-4 py-2 text-sm font-inter font-medium rounded-lg text-white"
            style={confirmStyle || { backgroundColor: '#055efd' }}>
            {loading ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LedgerManager() {
  const location = useLocation();
  const [businessId, setBusinessId] = useState(location.state?.businessId || '');
  const [ledgerData, setLedgerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Auto-fetch when navigated here from BusinessLookup
  useEffect(() => {
    if (location.state?.businessId) fetchLedger(); // eslint-disable-line react-hooks/exhaustive-deps
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [actionModal, setActionModal] = useState(null); // { type: 'recalculate'|'rebuild', dryRun: bool }
  const [actionResult, setActionResult] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchLedger = async () => {
    if (!businessId.trim()) return;
    setLoading(true);
    setError('');
    setLedgerData(null);
    setActionResult(null);
    try {
      const data = await adminService.getLedger(businessId.trim());
      setLedgerData(data);
    } catch (e) {
      setError(e.message || 'Failed to load ledger');
    } finally {
      setLoading(false);
    }
  };

  const runAction = async () => {
    if (!actionModal) return;
    setActionLoading(true);
    try {
      let result;
      if (actionModal.type === 'recalculate') {
        result = await adminService.recalculateLedger(businessId.trim(), actionModal.dryRun);
      } else {
        result = await adminService.rebuildLedger(businessId.trim(), actionModal.dryRun);
      }
      setActionResult({ type: actionModal.type, ...result });
      setActionModal(null);
      // Refresh ledger view after a real run
      if (!actionModal.dryRun) {
        await fetchLedger();
      }
    } catch (e) {
      setError(e.message || 'Action failed');
      setActionModal(null);
    } finally {
      setActionLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') fetchLedger();
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f9f9fb' }}>
      {/* Nav */}
      <nav className="bg-white shadow-sm" style={{ borderBottom: '1px solid #e5e7eb' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 space-x-4">
            <img src="/assets/logo---dark-with-color.png" alt="Remote Assistant" className="h-8 w-auto" />
            <Link to="/dashboard"
              className="text-sm font-inter"
              style={{ color: '#979ba4' }}>
              ← Dashboard
            </Link>
            <h1 className="text-xl font-montserrat font-semibold" style={{ color: '#222324' }}>
              Ledger Manager
            </h1>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">

        {/* Search */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6" style={{ border: '1px solid #e5e7eb' }}>
          <h2 className="text-lg font-montserrat font-semibold mb-4" style={{ color: '#222324' }}>
            Query Ledger
          </h2>
          <div className="flex space-x-3">
            <input
              type="text"
              value={businessId}
              onChange={e => setBusinessId(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Business ID"
              className="flex-1 px-3 py-2 text-sm font-inter rounded-lg"
              style={{ border: '1px solid #e5e7eb', outline: 'none' }}
            />
            <button
              onClick={fetchLedger}
              disabled={loading || !businessId.trim()}
              className="px-4 py-2 text-sm font-inter font-medium rounded-lg text-white"
              style={{ backgroundColor: loading ? '#6b9cfd' : '#055efd' }}>
              {loading ? 'Loading…' : 'Query'}
            </button>
          </div>
          {error && (
            <p className="mt-3 text-sm font-inter" style={{ color: '#dc2626' }}>{error}</p>
          )}
        </div>

        {/* Summary + actions */}
        {ledgerData && (
          <>
            <div className="bg-white rounded-2xl shadow-sm p-6 mb-6" style={{ border: '1px solid #e5e7eb' }}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-lg font-montserrat font-semibold" style={{ color: '#222324' }}>
                    {ledgerData.business_id}
                  </h2>
                  <p className="text-sm font-inter mt-1" style={{ color: '#979ba4' }}>
                    {ledgerData.summary.entry_count} entries · {ledgerData.summary.topup_count} top-ups · {ledgerData.summary.usage_count} usage
                  </p>
                </div>
                <SyncBadge inSync={ledgerData.in_sync} />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {[
                  { label: 'Ledger Balance', value: fmtSeconds(ledgerData.summary.balance_seconds) },
                  { label: 'Ledger (minutes)', value: fmtMinutes(ledgerData.summary.balance_minutes) },
                  { label: 'Cognito prepaid_minutes', value: ledgerData.cognito_prepaid_minutes != null ? `${ledgerData.cognito_prepaid_minutes} min` : '—' },
                  { label: 'Drift', value: ledgerData.cognito_prepaid_minutes != null ? `${Math.abs(Math.round(ledgerData.summary.balance_seconds / 60) - ledgerData.cognito_prepaid_minutes)} min` : '—' },
                ].map(({ label, value }) => (
                  <div key={label} className="rounded-xl p-4" style={{ backgroundColor: '#f9f9fb', border: '1px solid #e5e7eb' }}>
                    <p className="text-xs font-inter mb-1" style={{ color: '#979ba4' }}>{label}</p>
                    <p className="text-base font-montserrat font-semibold" style={{ color: '#222324' }}>{value}</p>
                  </div>
                ))}
              </div>

              {/* Action buttons */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setActionModal({ type: 'recalculate', dryRun: true })}
                  className="px-4 py-2 text-sm font-inter font-medium rounded-lg"
                  style={{ border: '1px solid #e5e7eb', color: '#222324', backgroundColor: '#fff' }}>
                  Recalculate (dry run)
                </button>
                <button
                  onClick={() => setActionModal({ type: 'recalculate', dryRun: false })}
                  className="px-4 py-2 text-sm font-inter font-medium rounded-lg text-white"
                  style={{ backgroundColor: '#055efd' }}>
                  Recalculate (apply)
                </button>
                <button
                  onClick={() => setActionModal({ type: 'rebuild', dryRun: true })}
                  className="px-4 py-2 text-sm font-inter font-medium rounded-lg"
                  style={{ border: '1px solid #e5e7eb', color: '#222324', backgroundColor: '#fff' }}>
                  Rebuild from calls (dry run)
                </button>
                <button
                  onClick={() => setActionModal({ type: 'rebuild', dryRun: false })}
                  className="px-4 py-2 text-sm font-inter font-medium rounded-lg text-white"
                  style={{ backgroundColor: '#dc2626' }}>
                  Rebuild from calls (apply)
                </button>
              </div>
            </div>

            {/* Action result */}
            {actionResult && (
              <div className="bg-white rounded-2xl shadow-sm p-6 mb-6" style={{ border: '1px solid #e5e7eb' }}>
                <h3 className="text-base font-montserrat font-semibold mb-3" style={{ color: '#222324' }}>
                  {actionResult.type === 'recalculate' ? 'Recalculate' : 'Rebuild'} result
                  {actionResult.dry_run && <span className="ml-2 text-xs font-inter" style={{ color: '#979ba4' }}>(dry run)</span>}
                </h3>

                {actionResult.type === 'recalculate' && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                      {[
                        { label: 'Entries scanned', value: actionResult.entries_scanned },
                        { label: 'Entries updated', value: actionResult.entries_updated },
                        { label: 'Final balance', value: `${fmtSeconds(actionResult.final_balance_seconds)} (${actionResult.final_balance_minutes} min)` },
                      ].map(({ label, value }) => (
                        <div key={label} className="rounded-xl p-4" style={{ backgroundColor: '#f9f9fb', border: '1px solid #e5e7eb' }}>
                          <p className="text-xs font-inter mb-1" style={{ color: '#979ba4' }}>{label}</p>
                          <p className="text-base font-montserrat font-semibold" style={{ color: '#222324' }}>{value}</p>
                        </div>
                      ))}
                    </div>
                    {actionResult.changes && actionResult.changes.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-xs font-inter">
                          <thead>
                            <tr style={{ color: '#979ba4', borderBottom: '1px solid #e5e7eb' }}>
                              {['Timestamp', 'Type', 'Old before', 'New before', 'Old after', 'New after'].map(h => (
                                <th key={h} className="text-left py-2 pr-4 font-medium">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {actionResult.changes.map((c, i) => (
                              <tr key={i} style={{ borderBottom: '1px solid #f3f4f6', color: '#222324' }}>
                                <td className="py-2 pr-4">{c.timestamp}</td>
                                <td className="py-2 pr-4"><EntryTypeBadge type={c.entry_type} /></td>
                                <td className="py-2 pr-4">{fmtSeconds(c.old_balance_before)}</td>
                                <td className="py-2 pr-4">{fmtSeconds(c.new_balance_before)}</td>
                                <td className="py-2 pr-4">{fmtSeconds(c.old_balance_after)}</td>
                                <td className="py-2 pr-4">{fmtSeconds(c.new_balance_after)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}

                {actionResult.type === 'rebuild' && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                      {[
                        { label: 'Calls found', value: actionResult.calls_found },
                        { label: 'Entries queued', value: actionResult.entries_queued },
                        { label: 'Skipped', value: actionResult.skipped },
                      ].map(({ label, value }) => (
                        <div key={label} className="rounded-xl p-4" style={{ backgroundColor: '#f9f9fb', border: '1px solid #e5e7eb' }}>
                          <p className="text-xs font-inter mb-1" style={{ color: '#979ba4' }}>{label}</p>
                          <p className="text-base font-montserrat font-semibold" style={{ color: '#222324' }}>{value}</p>
                        </div>
                      ))}
                    </div>
                    {actionResult.preview && actionResult.preview.length > 0 && (
                      <div className="overflow-x-auto">
                        <p className="text-xs font-inter mb-2" style={{ color: '#979ba4' }}>Preview (first 10 entries)</p>
                        <table className="min-w-full text-xs font-inter">
                          <thead>
                            <tr style={{ color: '#979ba4', borderBottom: '1px solid #e5e7eb' }}>
                              {['Call ID', 'Seconds used', 'Timestamp'].map(h => (
                                <th key={h} className="text-left py-2 pr-4 font-medium">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {actionResult.preview.map((p, i) => (
                              <tr key={i} style={{ borderBottom: '1px solid #f3f4f6', color: '#222324' }}>
                                <td className="py-2 pr-4 font-mono">{p.call_id}</td>
                                <td className="py-2 pr-4">{fmtSeconds(p.seconds_used)}</td>
                                <td className="py-2 pr-4">{p.timestamp}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Ledger entries table */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden" style={{ border: '1px solid #e5e7eb' }}>
              <div className="px-6 py-4" style={{ borderBottom: '1px solid #e5e7eb' }}>
                <h3 className="text-base font-montserrat font-semibold" style={{ color: '#222324' }}>
                  Ledger Entries
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm font-inter">
                  <thead>
                    <tr style={{ backgroundColor: '#f9f9fb', color: '#979ba4', borderBottom: '1px solid #e5e7eb' }}>
                      {['Timestamp', 'Type', 'Amount', 'Balance before', 'Balance after', 'Reference'].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerData.entries.map((entry, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9f9fb'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}>
                        <td className="px-4 py-3 text-xs" style={{ color: '#555' }}>{entry.timestamp}</td>
                        <td className="px-4 py-3"><EntryTypeBadge type={entry.entry_type} /></td>
                        <td className="px-4 py-3 font-medium" style={{ color: '#222324' }}>
                          {entry.entry_type === 'TOPUP'
                            ? `+${fmtSeconds(entry.topup_amount_seconds)}`
                            : `-${fmtSeconds(entry.seconds_used)}`}
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color: '#555' }}>{fmtSeconds(entry.balance_before)}</td>
                        <td className="px-4 py-3 text-xs" style={{ color: '#555' }}>{fmtSeconds(entry.balance_after)}</td>
                        <td className="px-4 py-3 text-xs font-mono" style={{ color: '#979ba4' }}>
                          {entry.entry_type === 'TOPUP' ? entry.topup_reference : entry.call_id}
                        </td>
                      </tr>
                    ))}
                    {ledgerData.entries.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-sm" style={{ color: '#979ba4' }}>
                          No ledger entries found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Confirm modal */}
      {actionModal && (
        <ConfirmModal
          title={actionModal.type === 'recalculate'
            ? `Recalculate ledger${actionModal.dryRun ? ' (dry run)' : ''}`
            : `Rebuild ledger from calls${actionModal.dryRun ? ' (dry run)' : ''}`}
          message={actionModal.type === 'recalculate'
            ? actionModal.dryRun
              ? 'Scan all entries and show what balance_before/balance_after corrections would be made, without writing anything.'
              : `This will overwrite balance_before and balance_after on all entries for ${businessId}. Continue?`
            : actionModal.dryRun
              ? 'Preview which call records would be re-queued to the ledger SQS, without sending anything.'
              : `Re-queue all ended calls for ${businessId} to the ledger SQS queue. SQS deduplication prevents double-counting. Continue?`}
          onConfirm={runAction}
          onCancel={() => setActionModal(null)}
          loading={actionLoading}
          confirmLabel={actionModal.dryRun ? 'Run dry run' : 'Apply'}
          confirmStyle={!actionModal.dryRun && actionModal.type === 'rebuild'
            ? { backgroundColor: '#dc2626' }
            : { backgroundColor: '#055efd' }}
        />
      )}
    </div>
  );
}
